import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { useCallback, useMemo } from 'react';
import { RadioGroupItem } from '@/components/ui/radio-group';
import { formatPrice, roundUpPrice } from '@/utils/circuits/prices/formatPrice';
import { useGetFileUrl } from '@/hooks/useFile';
import { useEstimateCarRental } from '@/hooks/useEstimateCarRental';
import { useImageCalculatedHeight } from '@/hooks/useImageCalculatedHeight';
import StyledExpandableText from '@/components/styled/StyledExpandableText';
import EstimateCarRentalElement, {
  CarRentalElementType,
} from './EstimateCarRentalElement';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useConvertPriceOnlyIfDateISProvided } from '@/hooks/useCurrencyExchange';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';

type CarCategoryTranslation = {
  category?: string;
  model?: string;
  description?: string;
  included?: string;
  notIncluded?: string;
  insurances?: Array<{
    name?: string;
    shortDescription?: string;
    longDescription?: string;
  }>;
  options?: Array<{
    name?: string;
    description?: string;
  }>;
  taxes?: Array<{
    name?: string;
  }>;
  oneWayFees?: Array<{
    name?: string;
    description?: string;
  }>;
};

interface EstimateOptionalCarRentalCategoryProps {
  benefitId: string;
  category: Required<
    CircuitDayFormData['steps'][number]['transport']['optionalBenefits'][number]
  >['carRental']['carCategories'][number] & {
    translations?: {
      [key in SupportedLocale]?: CarCategoryTranslation;
    };
  };
  nbDays?: number;
  deltaPrice: number;
  isChecked: boolean;
  shouldDisplayDescription: boolean;
  selectOptionalBenefit: () => void;
  accessToken?: string;
  stepId: string;
  selectedOptions: Record<string, SelectedEstimateOption>;
  setSelectedOptions: React.Dispatch<
    React.SetStateAction<Record<string, SelectedEstimateOption>>
  >;
  nbParticipants: number;
  commonShouldLimitInsurance?: boolean;
  language?: SupportedLocale;
  originalCurrency?: string;
  currency?: string;
  creationDate?: Date;
}

const EstimateOptionalCarRentalCategory = ({
  benefitId,
  category,
  deltaPrice,
  selectOptionalBenefit,
  isChecked,
  shouldDisplayDescription,
  nbDays = 1,
  accessToken,
  stepId,
  selectedOptions,
  setSelectedOptions,
  commonShouldLimitInsurance,
  nbParticipants,
  language,
  originalCurrency,
  currency = 'EUR',
  creationDate,
}: EstimateOptionalCarRentalCategoryProps) => {
  const { t } = useTranslation();
  const convertPrice = useConvertPriceOnlyIfDateISProvided(creationDate);
  const displayedDeltaPrice = useMemo(() => {
    const converted =
      convertPrice(deltaPrice, 'EUR', currency) ?? deltaPrice;
    return `${t('common.starting_from')} ${converted >= 0 ? '+' : '-'} ${formatPrice(Math.abs(roundUpPrice(converted)))} ${formatCurrency(currency)}`;
  }, [convertPrice, currency, deltaPrice, t]);

  const { carCategoryInformation } = useEstimateCarRental({
    carCategory: category,
    originalCurrency,
    currency,
    language,
  });

  const categoryImages = useMemo(() => {
    return category?.images ?? [];
  }, [category?.images]);

  const imageKey = useMemo(() => {
    return categoryImages.length > 0 ? categoryImages[0] : null;
  }, [categoryImages]);

  const longDescription = useMemo(() => {
    const translations = language
      ? category?.translations?.[language]
      : undefined;
    return shouldDisplayDescription
      ? (translations?.description ?? category?.description ?? '')
      : '';
  }, [
    category?.description,
    category?.translations,
    language,
    shouldDisplayDescription,
  ]);

  const { data: imageUrl } = useGetFileUrl(imageKey, false, accessToken);

  const { imgRef, calculatedHeight } = useImageCalculatedHeight({
    defaultHeight: 150,
  });

  const shouldLimitInsurance = useMemo(
    () =>
      (category?.displayCategoryOptions ?? false)
        ? (category?.shouldLimitInsurance ?? false)
        : (commonShouldLimitInsurance ?? false),
    [
      category?.displayCategoryOptions,
      category?.shouldLimitInsurance,
      commonShouldLimitInsurance,
    ],
  );

  const nbCars = useMemo(
    () => Math.ceil(nbParticipants / (category?.maxCapacity ?? 4)),
    [nbParticipants, category?.maxCapacity],
  );

  const categoryName = useMemo(() => {
    const translations = language
      ? category?.translations?.[language]
      : undefined;
    return `${nbCars} × ${translations?.category ?? category?.category ?? ''} - ${
      translations?.model ?? category?.model ?? ''
    }`;
  }, [
    category?.category,
    category?.model,
    nbCars,
    language,
    category?.translations,
  ]);

  const getCategoryTranslations = useCallback(
    (elementType: CarRentalElementType) => {
      if (!category || !language) return undefined;
      return category?.translations?.[language]?.[elementType];
    },
    [category, language],
  );

  return category ? (
    <div className="flex w-full flex-col gap-4 md:flex-row-reverse md:items-start">
      {imageUrl && (
        <div className="shrink-0 self-center md:self-start">
          <img
            ref={imgRef}
            src={imageUrl.url}
            className="h-auto max-h-56 w-72 min-w-72 rounded-lg object-cover object-center lg:w-80 lg:min-w-80"
          />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-4">
          <RadioGroupItem
            value={`${benefitId}-${category.id ?? ''}`}
            onClick={() => {
              selectOptionalBenefit();
            }}
            checked={isChecked}
            disabled={deltaPrice === Infinity}
          />
          <span>{categoryName}</span>
          <span className="text-sm font-semibold text-brand-700 whitespace-nowrap">
            {displayedDeltaPrice}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-normal text-gray-800">
            {t('estimates.details.carRentalElements.duration')}: {nbDays}{' '}
            {t('estimates.details.carRentalElements.days')}
          </span>
        </div>
        <StyledExpandableText
          text={longDescription}
          maxHeight={calculatedHeight - 50}
        />
        {Object.entries(carCategoryInformation ?? {})
          .filter(([, v]) => v)
          .map(([infoKey, infoValue], infoIndex) =>
            [
              t('estimates.details.carRentalElements.included'),
              t('estimates.details.carRentalElements.notIncluded'),
            ].includes(infoKey) && infoValue ? (
              <ul
                className="text-sm font-normal text-gray-800 list-disc"
                key={`estimate-step-information-${infoKey}-${infoIndex}`}
              >
                {infoKey} :
                {infoValue.split('\n').map((bulletPoint, indexBulletPoint) => (
                  <li
                    key={`estimate-step-information-${infoKey}-${indexBulletPoint}`}
                    className="ml-4"
                  >
                    {bulletPoint}
                  </li>
                ))}
              </ul>
            ) : (
              <p
                className="text-sm font-normal text-gray-800"
                key={`estimate-step-information-${infoKey}-${infoIndex}`}
              >
                {infoKey} : {infoValue}
              </p>
            ),
          )}
        <EstimateCarRentalElement
          elementType="insurances"
          data={category?.insurances}
          stepId={stepId}
          benefitId={benefitId}
          carCategoryId={category?.id ?? ''}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          canCheckOnlyOneElement={shouldLimitInsurance}
          nbDays={nbDays}
          nbCars={nbCars}
          currency={currency}
          originalCurrency={originalCurrency}
          translations={getCategoryTranslations('insurances')}
          language={language}
        />
        <EstimateCarRentalElement
          elementType="options"
          data={category?.options}
          stepId={stepId}
          benefitId={benefitId}
          carCategoryId={category?.id ?? ''}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          canCheckOnlyOneElement={false}
          nbDays={nbDays}
          nbCars={nbCars}
          currency={currency}
          originalCurrency={originalCurrency}
          translations={getCategoryTranslations('options')}
          language={language}
        />
        <EstimateCarRentalElement
          elementType="taxes"
          data={category?.taxes}
          stepId={stepId}
          benefitId={benefitId}
          carCategoryId={category?.id ?? ''}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          canCheckOnlyOneElement={false}
          nbDays={nbDays}
          nbCars={nbCars}
          currency={currency}
          originalCurrency={originalCurrency}
          translations={getCategoryTranslations('taxes')}
          language={language}
        />
        <EstimateCarRentalElement
          elementType="oneWayFees"
          data={category?.oneWayFees}
          stepId={stepId}
          benefitId={benefitId}
          carCategoryId={category?.id ?? ''}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          canCheckOnlyOneElement={false}
          nbDays={nbDays}
          nbCars={nbCars}
          currency={currency}
          originalCurrency={originalCurrency}
          translations={getCategoryTranslations('oneWayFees')}
          language={language}
        />
      </div>
    </div>
  ) : null;
};

export default EstimateOptionalCarRentalCategory;
